// ════════════════════════════════════════════════════════════
//  fxTex.js — every painted texture the battle needs, cached
// ════════════════════════════════════════════════════════════
//  One canvas per KEY, made once, reused forever (the being kit's
//  law: an uncached canvasTex is a GPU upload every mount). All
//  variety is seeded — the same scorch looks the same every load.
// ════════════════════════════════════════════════════════════
import * as THREE from "three";
import { canvasTex, mulberry32 } from "../ieBeingModels.jsx";
import { drawSealBody, drawSealFour } from "../data/seals.js";

// ── THE SEALS (data/seals.js says what they look like; Naew replaces it) ──
// body = rim + heart, white (the caster's colour tints it); four = the
// Aenergies in their own canon colours (drawn untinted, facing the viewer)
export const sealBodyTex = (seal) => canvasTex("seal_body_v1_" + seal.rim + "_" + seal.heart, 256, 256, (g, w) => drawSealBody(g, w, seal));
export const sealFourTex = (seal) => canvasTex("seal_four_v1_" + seal.key.slice(-4), 256, 256, (g, w) => drawSealFour(g, w, seal));

// soft round glow — the sprite behind every spark, shot and flare
export const glowTex = () => canvasTex("clash_glow_v1", 128, 128, (g) => {
  const r = g.createRadialGradient(64, 64, 0, 64, 64, 64);
  r.addColorStop(0, "rgba(255,255,255,1)");
  r.addColorStop(0.22, "rgba(255,255,255,0.75)");
  r.addColorStop(0.55, "rgba(255,255,255,0.18)");
  r.addColorStop(1, "rgba(255,255,255,0)");
  g.fillStyle = r; g.fillRect(0, 0, 128, 128);
});

// a thin bright ring — shockwaves, telegraphs, the Ascent halo
export const ringTex = () => canvasTex("clash_ring_v1", 256, 256, (g) => {
  const r = g.createRadialGradient(128, 128, 88, 128, 128, 126);
  r.addColorStop(0, "rgba(255,255,255,0)");
  r.addColorStop(0.55, "rgba(255,255,255,0.9)");
  r.addColorStop(0.7, "rgba(255,255,255,0.35)");
  r.addColorStop(1, "rgba(255,255,255,0)");
  g.fillStyle = r; g.fillRect(0, 0, 256, 256);
});

// a telegraph disc: faint fill + hard edge + inner tick marks
export const telegraphTex = () => canvasTex("clash_telegraph_v1", 256, 256, (g) => {
  g.clearRect(0, 0, 256, 256);
  const f = g.createRadialGradient(128, 128, 0, 128, 128, 124);
  f.addColorStop(0, "rgba(255,255,255,0.05)");
  f.addColorStop(0.85, "rgba(255,255,255,0.22)");
  f.addColorStop(1, "rgba(255,255,255,0)");
  g.fillStyle = f; g.beginPath(); g.arc(128, 128, 124, 0, 7); g.fill();
  g.strokeStyle = "rgba(255,255,255,0.95)"; g.lineWidth = 5;
  g.beginPath(); g.arc(128, 128, 118, 0, 7); g.stroke();
  g.lineWidth = 3;
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    g.beginPath(); g.moveTo(128 + Math.cos(a) * 96, 128 + Math.sin(a) * 96);
    g.lineTo(128 + Math.cos(a) * 110, 128 + Math.sin(a) * 110); g.stroke();
  }
});

// ── THE SCARS — what a fight leaves on the floor ─────────────
// scorch: a burnt blot with a hot, ragged rim
export const scorchTex = (variant = 0) => canvasTex("clash_scorch_v1_" + variant, 256, 256, (g) => {
  const R = mulberry32(410 + variant * 17);
  g.clearRect(0, 0, 256, 256);
  for (let i = 0; i < 26; i++) {
    const a = R() * 7, d = R() * 50;
    const x = 128 + Math.cos(a) * d, y = 128 + Math.sin(a) * d, r = 40 + R() * 50;
    const gr = g.createRadialGradient(x, y, 0, x, y, r);
    gr.addColorStop(0, "rgba(6,3,8,0.34)");
    gr.addColorStop(1, "rgba(6,3,8,0)");
    g.fillStyle = gr; g.beginPath(); g.arc(x, y, r, 0, 7); g.fill();
  }
  // glass flecks — the ground fused where the heat was worst
  for (let i = 0; i < 40; i++) {
    g.fillStyle = `rgba(255,${180 + R() * 60 | 0},120,${0.05 + R() * 0.12})`;
    const a = R() * 7, d = R() * 70;
    g.fillRect(128 + Math.cos(a) * d, 128 + Math.sin(a) * d, 2 + R() * 3, 2 + R() * 3);
  }
});

// crater: radial cracks from a centre, glowing in the seams
export const crackTex = (variant = 0) => canvasTex("clash_crack_v1_" + variant, 512, 512, (g) => {
  const R = mulberry32(911 + variant * 31);
  g.clearRect(0, 0, 512, 512);
  const dark = g.createRadialGradient(256, 256, 0, 256, 256, 150);
  dark.addColorStop(0, "rgba(4,2,6,0.65)"); dark.addColorStop(1, "rgba(4,2,6,0)");
  g.fillStyle = dark; g.beginPath(); g.arc(256, 256, 150, 0, 7); g.fill();
  // the same seed drives both passes, so the hot seam lands exactly
  // on top of the dark crack body
  const crack = (R, x, y, a, len, w, depth) => {
    g.lineWidth = w; g.beginPath(); g.moveTo(x, y);
    let cx = x, cy = y;
    const segs = 6;
    for (let i = 0; i < segs; i++) {
      a += (R() - 0.5) * 0.7;
      cx += Math.cos(a) * len / segs; cy += Math.sin(a) * len / segs;
      g.lineTo(cx, cy);
    }
    g.stroke();
    const br1 = R(), br2 = R(), sa = R(), sb = R(), sc = R();
    if (depth > 0 && br1 < 0.8) crack(R, cx, cy, a + (sa - 0.5) * 1.6, len * 0.55, w * 0.6, depth - 1);
    if (depth > 0 && br2 < 0.5) crack(R, (x + cx) / 2, (y + cy) / 2, a + (sb < 0.5 ? 1 : -1) * (0.5 + sc), len * 0.45, w * 0.55, depth - 1);
  };
  const n = 9 + Math.floor(R() * 4);
  for (const pass of [0, 1]) {
    const R2 = mulberry32(911 + variant * 31 + 7);
    g.strokeStyle = pass === 0 ? "rgba(2,1,4,0.85)" : "rgba(255,255,255,0.75)";
    g.shadowColor = "rgba(255,255,255,0.9)"; g.shadowBlur = pass ? 8 : 0;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + R2() * 0.4;
      const len = 120 + R2() * 110;
      crack(R2, 256 + Math.cos(a) * 18, 256 + Math.sin(a) * 18, a, len, pass === 0 ? 7 : 2, 2);
    }
  }
  g.shadowBlur = 0;
});

// beam groove: a long burnt furrow
export const grooveTex = () => canvasTex("clash_groove_v1", 512, 64, (g) => {
  const R = mulberry32(77);
  g.clearRect(0, 0, 512, 64);
  for (let i = 0; i < 90; i++) {
    const x = R() * 512, w = 20 + R() * 60;
    const gr = g.createLinearGradient(0, 8, 0, 56);
    gr.addColorStop(0, "rgba(5,3,7,0)"); gr.addColorStop(0.5, `rgba(5,3,7,${0.18 + R() * 0.2})`); gr.addColorStop(1, "rgba(5,3,7,0)");
    g.fillStyle = gr; g.fillRect(x - w / 2, 0, w, 64);
  }
  g.strokeStyle = "rgba(255,255,255,0.55)"; g.lineWidth = 2;
  g.beginPath(); g.moveTo(0, 32);
  for (let x = 0; x <= 512; x += 16) g.lineTo(x, 32 + (R() - 0.5) * 8);
  g.stroke();
});

// a footprint / skid smudge
export const smudgeTex = () => canvasTex("clash_smudge_v1", 64, 64, (g) => {
  const r = g.createRadialGradient(32, 32, 0, 32, 32, 30);
  r.addColorStop(0, "rgba(10,6,14,0.5)"); r.addColorStop(1, "rgba(10,6,14,0)");
  g.fillStyle = r; g.beginPath(); g.ellipse(32, 32, 30, 18, 0, 0, 7); g.fill();
});

// the Ascent rune left where a being transcended
export const runeTex = () => canvasTex("clash_rune_v1", 512, 512, (g) => {
  g.clearRect(0, 0, 512, 512);
  g.strokeStyle = "rgba(255,255,255,0.9)";
  g.shadowColor = "rgba(255,255,255,1)"; g.shadowBlur = 10;
  g.lineWidth = 4; g.beginPath(); g.arc(256, 256, 230, 0, 7); g.stroke();
  g.lineWidth = 2; g.beginPath(); g.arc(256, 256, 205, 0, 7); g.stroke();
  // three nested triangles: Beb, Champion, Lord
  for (let k = 0; k < 3; k++) {
    const r = 190 - k * 50, rot = k * 0.35;
    g.beginPath();
    for (let i = 0; i <= 3; i++) {
      const a = rot - Math.PI / 2 + (i / 3) * Math.PI * 2;
      const x = 256 + Math.cos(a) * r, y = 256 + Math.sin(a) * r;
      i ? g.lineTo(x, y) : g.moveTo(x, y);
    }
    g.stroke();
  }
  for (let i = 0; i < 24; i++) {
    const a = (i / 24) * Math.PI * 2;
    g.beginPath(); g.moveTo(256 + Math.cos(a) * 205, 256 + Math.sin(a) * 205);
    g.lineTo(256 + Math.cos(a) * 230, 256 + Math.sin(a) * 230); g.stroke();
  }
  g.shadowBlur = 0;
});

// ── WORDS IN THE WORLD — painted, never <Html> (the perf law) ──
// Cached by text+colour. Damage numbers are a small set; a session
// stays well under the cache cap.
const TEXT_CACHE = new Map();
export function textTex(text, color = "#ffffff", big = false) {
  const key = text + "|" + color + "|" + (big ? 1 : 0);
  if (TEXT_CACHE.has(key)) return TEXT_CACHE.get(key);
  if (TEXT_CACHE.size > 400) {
    const first = TEXT_CACHE.keys().next().value;
    TEXT_CACHE.get(first).tex.dispose(); TEXT_CACHE.delete(first);
  }
  const fs = big ? 76 : 54;
  const c = document.createElement("canvas");
  const g = c.getContext("2d");
  const font = `900 ${fs}px "Bebas Neue", "Impact", "Arial Black", sans-serif`;
  g.font = font;
  const w = Math.ceil(g.measureText(text).width) + 36;
  c.width = w; c.height = fs + 30;
  g.font = font; g.textAlign = "center"; g.textBaseline = "middle";
  g.lineJoin = "round";
  g.strokeStyle = "rgba(8,4,14,0.95)"; g.lineWidth = big ? 12 : 9;
  g.strokeText(text, w / 2, c.height / 2);
  g.shadowColor = color; g.shadowBlur = 14;
  g.fillStyle = color; g.fillText(text, w / 2, c.height / 2);
  g.shadowBlur = 0;
  g.fillStyle = "rgba(255,255,255,0.35)"; g.fillText(text, w / 2, c.height / 2 - 2);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  const v = { tex, aspect: c.width / c.height };
  TEXT_CACHE.set(key, v);
  return v;
}
